import React, { useCallback, useEffect, useState } from 'react';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Search, Loader2, CheckCircle2, RotateCcw, Factory, Truck, Package, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const STATUS = {
  open: { label: 'open', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  partly_used: { label: 'partly used', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  ready_to_close: { label: 'ready to close', cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  closed: { label: 'closed — sent to accounts', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

const EMPTY_CLOSE = { item_id: '', scrap_qty: '', scrap_reason: '', rejection_qty: '', rejection_reason: '' };

const closureExtra = (rc, f) =>
  Math.max(0, (parseFloat(f.scrap_qty) || 0) + (parseFloat(f.rejection_qty) || 0) - (rc?.available_qty || 0));

function ClosureQtyRow({ label, kind, form, setForm, reasons }) {
  const qtyKey = `${kind}_qty`, reasonKey = `${kind}_reason`;
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs">{label}</Label>
        <Input type="number" min="0" value={form[qtyKey]} className="h-8 text-xs" placeholder="0"
          onChange={e => setForm(f => ({ ...f, [qtyKey]: e.target.value }))} data-testid={`rc-close-${kind}-qty`} />
      </div>
      <div>
        <Label className="text-xs">Reason</Label>
        <Select value={form[reasonKey]} onValueChange={v => setForm(f => ({ ...f, [reasonKey]: v }))}
          disabled={!(parseFloat(form[qtyKey]) > 0)}>
          <SelectTrigger className="h-8 text-xs" data-testid={`rc-close-${kind}-reason`}><SelectValue placeholder="Pick a reason" /></SelectTrigger>
          <SelectContent>
            {reasons.map(r => <SelectItem key={r.id} value={r.name} className="text-xs">{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function RouteCardsPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [detail, setDetail] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [closeRemarks, setCloseRemarks] = useState('');
  const [closeForm, setCloseForm] = useState(EMPTY_CLOSE);
  const [reasons, setReasons] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inventoryAPI.listRouteCards({ search: search || undefined, status });
      setCards(res.data?.route_cards || []);
    } catch { toast.error('Failed to load route cards'); }
    finally { setLoading(false); }
  }, [search, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    inventoryAPI.rejectionReasons().then(r => setReasons(r.data?.reasons || [])).catch(() => {});
  }, []);

  const openDetail = async (rc) => {
    try {
      const res = await inventoryAPI.getRouteCard(rc);
      setDetail(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not load the route card'); }
  };

  const confirmClose = async () => {
    setBusy(true);
    try {
      const res = await inventoryAPI.closeRouteCard(closeTarget.route_card_number, {
        remarks: closeRemarks, item_id: closeForm.item_id || undefined,
        scrap_qty: parseFloat(closeForm.scrap_qty) || 0, scrap_reason: closeForm.scrap_reason,
        rejection_qty: parseFloat(closeForm.rejection_qty) || 0, rejection_reason: closeForm.rejection_reason,
      });
      toast.success(res.data.message);
      setCloseTarget(null); setCloseRemarks(''); setCloseForm(EMPTY_CLOSE);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not close the route card'); }
    finally { setBusy(false); }
  };

  const reopen = async (rc) => {
    setBusy(true);
    try {
      const res = await inventoryAPI.reopenRouteCard(rc);
      toast.success(res.data.message);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not reopen the route card'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="route-cards-page">
      <div>
        <h1 className="text-xl font-semibold">Route Cards</h1>
        <p className="text-xs text-muted-foreground">
          Follow a production batch from receipt to delivery challan, and close the hard copy for accounts.
        </p>
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} className="h-9 text-xs pl-7"
                placeholder="Route card no. / part / work order" data-testid="rc-search" />
            </div>
          </div>
          <div className="w-56">
            <Label className="text-xs">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 text-xs" data-testid="rc-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All route cards</SelectItem>
                <SelectItem value="open">Open (nothing used yet)</SelectItem>
                <SelectItem value="partly_used">Partly used</SelectItem>
                <SelectItem value="ready_to_close">Ready to close</SelectItem>
                <SelectItem value="closed">Closed — sent to accounts</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={load} data-testid="rc-refresh">Refresh</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">{cards.length} route card(s)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2" data-testid="rc-list">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : cards.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">No route cards match this filter</p>
          ) : cards.map(rc => (
            <div key={rc.route_card_number} className="border rounded-lg p-3" data-testid={`rc-row-${rc.route_card_number}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button className="text-sm font-semibold font-mono text-blue-700 hover:underline"
                      onClick={() => openDetail(rc.route_card_number)} data-testid={`rc-open-${rc.route_card_number}`}>
                      {rc.route_card_number}
                    </button>
                    <Badge className={`text-[10px] ${STATUS[rc.status]?.cls}`}>{STATUS[rc.status]?.label}</Badge>
                    {rc.hard_copy_closed && (
                      <span className="text-[10px] text-muted-foreground">
                        ticked by {rc.closed_by}{rc.closed_at ? ` on ${new Date(rc.closed_at).toLocaleDateString()}` : ''}
                      </span>
                    )}
                    {(rc.closure_rejection_qty > 0 || rc.closure_scrap_qty > 0) && (
                      <span className="text-[10px] text-red-700" data-testid={`rc-closure-losses-${rc.route_card_number}`}>
                        {rc.closure_rejection_qty > 0 && `${fmt(rc.closure_rejection_qty)} to MRB `}
                        {rc.closure_scrap_qty > 0 && `${fmt(rc.closure_scrap_qty)} scrapped`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {(rc.items || []).map(i => `${i.item_unique_id || ''} ${i.item_name || ''}`.trim()).join(', ')}
                    {rc.wo_numbers?.length ? ` · WO ${rc.wo_numbers.join(', ')}` : ''}
                  </p>
                  <div className="flex gap-3 mt-1 text-[11px] flex-wrap">
                    <span><Package className="w-3 h-3 inline mr-0.5" />produced <b>{fmt(rc.produced_qty)}</b></span>
                    <span className="text-amber-700">balance in stock <b>{fmt(rc.available_qty)}</b></span>
                    <span className="text-blue-700"><Factory className="w-3 h-3 inline mr-0.5" />consumed <b>{fmt(rc.consumed_qty)}</b></span>
                    <span className="text-teal-700"><Truck className="w-3 h-3 inline mr-0.5" />dispatched <b>{fmt(rc.dispatched_qty)}</b></span>
                  </div>
                  {(rc.consumed_by || []).length > 0 && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      Consumed by {rc.consumed_by.map(w => `${w.wo_number} (${fmt(w.quantity)} → ${w.produced_item})`).join(', ')}
                    </p>
                  )}
                  {(rc.dispatched_on || []).length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Dispatched on {rc.dispatched_on.map(d => `${d.dc_number} (${fmt(d.quantity)} ${d.item_name})`).join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  {!rc.hard_copy_closed ? (
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => { setCloseTarget(rc); setCloseRemarks(''); setCloseForm({ ...EMPTY_CLOSE, item_id: rc.items?.[0]?.item_id || '' }); }} disabled={busy}
                      data-testid={`rc-close-${rc.route_card_number}`}>
                      <ClipboardCheck className="w-3 h-3 mr-1" /> Tick as closed
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => reopen(rc.route_card_number)} disabled={busy}
                      data-testid={`rc-reopen-${rc.route_card_number}`}>
                      <RotateCcw className="w-3 h-3 mr-1" /> Reopen
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Trace detail */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm font-mono">Route card {detail?.route_card_number}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-xs" data-testid="rc-detail">
              <div className="flex gap-3 flex-wrap">
                <Badge className={`text-[10px] ${STATUS[detail.status]?.cls}`}>{STATUS[detail.status]?.label}</Badge>
                <span>produced <b>{fmt(detail.produced_qty)}</b></span>
                <span>in stock <b>{fmt(detail.available_qty)}</b></span>
                <span>consumed <b>{fmt(detail.consumed_qty)}</b></span>
                <span>dispatched <b>{fmt(detail.dispatched_qty)}</b></span>
              </div>

              <div>
                <p className="font-semibold mb-1">Produced batches</p>
                <ul className="space-y-0.5">
                  {detail.lots.map((l, i) => (
                    <li key={i} className="text-[11px]">
                      {l.item_unique_id} {l.item_name} · {fmt(l.quantity)} (balance {fmt(l.available_quantity)}) ·
                      WO {l.wo_number} · {l.godown_name}
                      {l.serial_start ? ` · sr ${l.serial_start}-${l.serial_end}` : ''}
                      {l.rm_trace_summary ? ` · RM ${l.rm_trace_summary}` : ''}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-1">Consumed by work orders</p>
                {detail.consumed_by.length === 0 ? <p className="text-muted-foreground text-[11px]">Not consumed yet</p> : (
                  <ul className="space-y-0.5">
                    {detail.consumed_by.map((w, i) => (
                      <li key={i} className="text-[11px]">{w.wo_number} · {fmt(w.quantity)} · produced {w.produced_item}{w.date ? ` · ${w.date}` : ''}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="font-semibold mb-1">Downstream route cards</p>
                {detail.downstream_route_cards.length === 0 ? <p className="text-muted-foreground text-[11px]">None</p> : (
                  <ul className="space-y-0.5">
                    {detail.downstream_route_cards.map((x, i) => (
                      <li key={i} className="text-[11px]">
                        via {x.via_wo} → <span className="font-mono">{x.route_card_number}</span> · {x.item_name} · {fmt(x.quantity)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <p className="font-semibold mb-1">Dispatched on</p>
                {detail.dispatched_on.length === 0 ? <p className="text-muted-foreground text-[11px]">Not dispatched yet</p> : (
                  <ul className="space-y-0.5">
                    {detail.dispatched_on.map((d, i) => (
                      <li key={i} className="text-[11px]">
                        {d.dc_number} · {d.customer_name} · {d.item_name} · {fmt(d.quantity)}
                        {d.dispatch_date ? ` · ${new Date(d.dispatch_date).toLocaleDateString()}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {detail.hard_copy_closed && (
                <p className="text-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
                  Hard copy closed by {detail.closed_by} on {new Date(detail.closed_at).toLocaleString()}
                  {detail.closure_remarks ? ` — ${detail.closure_remarks}` : ''}
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Closure confirmation */}
      <Dialog open={!!closeTarget} onOpenChange={() => setCloseTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-sm">Close route card {closeTarget?.route_card_number}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              Records that the hard copy has been closed by production and sent to accounts for reconciliation.
              {closeTarget?.available_qty > 0 && (
                <span className="block text-amber-700 mt-1">
                  Note: {fmt(closeTarget.available_qty)} is still in stock against this route card.
                </span>
              )}
            </p>
            {(closeTarget?.items || []).length > 1 && (
              <div>
                <Label className="text-xs">Item for scrap / rejection</Label>
                <Select value={closeForm.item_id} onValueChange={v => setCloseForm(f => ({ ...f, item_id: v }))}>
                  <SelectTrigger className="h-8 text-xs" data-testid="rc-close-item"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {closeTarget.items.map(it => <SelectItem key={it.item_id} value={it.item_id} className="text-xs">{it.item_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <ClosureQtyRow label="Rejection qty (goes to MRB)" kind="rejection" form={closeForm} setForm={setCloseForm} reasons={reasons} />
            <ClosureQtyRow label="Scrap qty (straight to Scrap Yard)" kind="scrap" form={closeForm} setForm={setCloseForm} reasons={reasons} />
            {closureExtra(closeTarget, closeForm) > 0 && (
              <p className="text-[11px] text-amber-700" data-testid="rc-close-extra-note">
                {fmt(closureExtra(closeTarget, closeForm))} pcs are more than the {fmt(closeTarget.available_qty)} left in stock
                — they'll be booked as extra bad pieces.
              </p>
            )}
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={closeRemarks} onChange={e => setCloseRemarks(e.target.value)} rows={2}
                className="text-xs" placeholder="e.g. hard copy handed to accounts on 20/06" data-testid="rc-close-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCloseTarget(null)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={confirmClose} disabled={busy}
              data-testid="rc-confirm-close">
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Tick as closed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
